import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Show } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { posterGradient } from '@/lib/posters';
import { Skeleton } from '@/components/Skeleton';
import { PosterRow } from '@/components/Poster';
import { buttonVariants } from '@/components/ui/Button';
import { ChevronLeft, ChevronRight, Info } from '@/components/icons';
import { catalogApi, meApi } from '@/features/catalog/api';
import { useAuth } from '@/features/auth/AuthContext';

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-surface/70 px-2.5 py-1 text-xs font-medium text-ink-muted backdrop-blur">
      {children}
    </span>
  );
}

function Hero({ shows }: { shows: Show[] }) {
  const [i, setI] = useState(0);
  if (shows.length === 0) return null;
  const show = shows[i % shows.length]!;

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0" style={{ backgroundImage: posterGradient(show.id) }} />
        <div className="absolute -right-24 top-1/2 h-[34rem] w-[34rem] -translate-y-1/2 rounded-full bg-brand/20 blur-3xl animate-float-slow" />
        <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/75 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-hero-fade" />
      </div>

      <div className="container-page relative z-10 flex min-h-[56vh] flex-col justify-end py-14 md:min-h-[62vh] md:py-20">
        <div key={show.id} className="max-w-xl animate-fade-in">
          <p className="eyebrow mb-3">Featured</p>
          <h1 className="font-display text-display-sm font-extrabold leading-[1.05] text-ink md:text-display">
            {show.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Chip>JP · EN audio</Chip>
            {show.genres.slice(0, 2).map((g) => (
              <Chip key={g.id}>{g.name}</Chip>
            ))}
            <Chip>{show.status === 'completed' ? 'Complete' : 'Ongoing'}</Chip>
          </div>
          <p className="mt-4 max-w-prose text-ink-muted line-clamp-3">{show.description}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/shows/${show.slug}`}
              className={buttonVariants({ variant: 'primary', size: 'lg' })}
            >
              <Info size={18} />
              More info
            </Link>
          </div>

          {shows.length > 1 && (
            <div className="mt-9 flex items-center gap-3">
              <button
                type="button"
                aria-label="Previous"
                onClick={() => setI((v) => (v - 1 + shows.length) % shows.length)}
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-surface/70 text-ink-muted backdrop-blur hover:text-ink"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={() => setI((v) => (v + 1) % shows.length)}
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-surface/70 text-ink-muted backdrop-blur hover:text-ink"
              >
                <ChevronRight size={18} />
              </button>
              <div className="ml-1 flex items-center gap-1.5">
                {shows.map((s, idx) => (
                  <span
                    key={s.id}
                    className={cn(
                      'h-1.5 rounded-full transition-all',
                      idx === i % shows.length ? 'w-6 bg-brand-gradient' : 'w-1.5 bg-border-strong',
                    )}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function RowSkeleton() {
  return (
    <div>
      <Skeleton className="mb-3 h-5 w-40" />
      <div className="flex gap-3 overflow-hidden">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="w-[42vw] shrink-0 sm:w-[180px] md:w-[164px] lg:w-[176px]">
            <Skeleton className="aspect-[2/3] w-full rounded-card" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function BrowsePage() {
  const { user } = useAuth();
  const browse = useQuery({ queryKey: ['browse'], queryFn: catalogApi.browse });
  const cont = useQuery({
    queryKey: ['continue'],
    queryFn: meApi.continueWatching,
    enabled: Boolean(user),
  });
  const list = useQuery({ queryKey: ['my-list'], queryFn: meApi.list, enabled: Boolean(user) });

  if (browse.isLoading) {
    return (
      <div className="pb-16">
        <Skeleton className="h-[56vh] w-full" />
        <div className="container-page mt-8 space-y-9">
          <RowSkeleton />
          <RowSkeleton />
          <RowSkeleton />
        </div>
      </div>
    );
  }

  if (browse.isError || !browse.data) {
    return (
      <div className="container-page py-20 text-center text-ink-muted">
        Couldn't load the catalog. Please try again.
      </div>
    );
  }

  const continueShows = (cont.data?.items ?? []).map((it) => it.show);
  const myListShows = list.data?.shows ?? [];

  return (
    <div className="pb-16">
      <Hero shows={browse.data.featured} />
      <div className="container-page mt-8 space-y-9">
        {continueShows.length > 0 && <PosterRow title="Continue Watching" shows={continueShows} />}
        {myListShows.length > 0 && <PosterRow title="My List" shows={myListShows} />}
        {browse.data.rows.map((row) => (
          <PosterRow key={row.key} title={row.title} shows={row.shows} />
        ))}
      </div>
    </div>
  );
}
